// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Client-side media encryption (Phase 6). Files are encrypted with a fresh
// AES-256-GCM key BEFORE upload, so the homeserver only ever stores opaque
// ciphertext. The key + IV ride inside the E2E-sealed message content, so only
// room members can decrypt the blob. Uses the browser's native WebCrypto
// (available in any secure context, incl. localhost).

// Only the tiny key/IV are base64'd (never the large ciphertext), so the spread
// can't overflow the stack.
const toB64 = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export interface MediaCrypto {
  key: string; // base64 raw AES-256 key
  iv: string; // base64 96-bit nonce
  mimetype: string;
  size: number;
}

// encryptBlob returns the ciphertext plus the key/IV to embed in the message.
export async function encryptBlob(data: ArrayBuffer, mimetype: string): Promise<{ ciphertext: ArrayBuffer; file: MediaCrypto }> {
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  const raw = await crypto.subtle.exportKey("raw", key);
  return { ciphertext, file: { key: toB64(raw), iv: toB64(iv.buffer), mimetype, size: data.byteLength } };
}

// decryptBlob reverses encryptBlob.
export async function decryptBlob(ciphertext: ArrayBuffer, keyB64: string, ivB64: string): Promise<ArrayBuffer> {
  const key = await crypto.subtle.importKey("raw", fromB64(keyB64), { name: "AES-GCM" }, false, ["decrypt"]);
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: fromB64(ivB64) }, key, ciphertext);
}

// mediaMsgtype picks the message type from a MIME type.
export function mediaMsgtype(mimetype: string): "m.image" | "m.video" | "m.audio" | "m.file" {
  if (mimetype.startsWith("image/")) return "m.image";
  if (mimetype.startsWith("video/")) return "m.video";
  if (mimetype.startsWith("audio/")) return "m.audio";
  return "m.file";
}
